import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import { RegionPage } from "@/components/RegionPage";
import hero from "@/assets/original/hero-shade.jpg";

export const Route = createFileRoute("/central-texas")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Central Texas Awnings & Patio Covers | Texas Made Shade",
      description:
        "Custom welded awnings, sail shades, patio & pergola covers and outdoor curtains for Central Texas — Austin, San Marcos, New Braunfels.",
      keywords:
        "Central Texas awnings, awning company Central Texas, Austin awnings, San Marcos awnings, New Braunfels awnings, Seguin awnings, sail shades Central Texas, patio covers Central Texas, pergola covers Central Texas",
      image: hero,
      socialTitle: "Central Texas Awnings & Shade — Texas Made Shade",
      socialDescription:
        "Welded aluminum + Sunbrella canvas. Serving Austin, San Marcos, New Braunfels, Seguin and Central Texas.",
    }),
    scripts: pageHeadScripts({
      path: "/central-texas",
      name: "Central Texas Awnings & Patio Covers",
      description: "Custom welded awnings, sail shades, patio & pergola covers and outdoor curtains for Central Texas — Austin, San Marcos, New Braunfels.",
      faqs: [
        { q: "Do you really drive to Austin and Round Rock from Seguin?", a: "Yes. We install across the entire I-35 corridor every week and price the trip into the quote — no separate travel fee surprises." },
        { q: "Will a fabric awning survive Central Texas hail and wind?", a: "Our welded aluminum frames are engineered for sustained 60+ mph gusts. We use Sunbrella® marine-grade canvas with reinforced corners; small hail typically sheds with no damage." },
        { q: "How long does a Central Texas custom awning project take?", a: "From signed quote to install we average 3–5 weeks depending on size and finish. Patterning is on-site; fabrication and sewing happen in Seguin." },
      ],
    }),
  }),
  component: () => (
    <RegionPage
      region="Central Texas"
      tagline="From Austin down through Seguin, we build heavy-duty shade and covers engineered for hot summers, sudden storms and big temperature swings."
      intro="Central Texas weather is unforgiving — triple-digit summers, hail-bearing thunderstorms and gusty cold fronts. Every awning, sail shade and pergola cover we build for the I-35 corridor uses welded aluminum frames and marine-grade Sunbrella® fabric, patterned and sewn at our shop in Seguin and installed by the same crew that builds it. Whether you're in downtown Austin, a Hill Country fringe in Dripping Springs or a Guadalupe County ranch, we've spec'd shade for it before."
      cities={[
        { to: "/austin", name: "Austin", county: "Travis Co." },
        { to: "/san-marcos", name: "San Marcos", county: "Hays Co." },
        { to: "/new-braunfels", name: "New Braunfels", county: "Comal Co." },
        { to: "/seguin", name: "Seguin", county: "Guadalupe Co." },
        { to: "/round-rock", name: "Round Rock", county: "Williamson Co." },
        { to: "/cedar-park", name: "Cedar Park", county: "Williamson Co." },
        { to: "/pflugerville", name: "Pflugerville", county: "Travis Co." },
        { to: "/georgetown", name: "Georgetown", county: "Williamson Co." },
        { to: "/buda", name: "Buda", county: "Hays Co." },
        { to: "/kyle", name: "Kyle", county: "Hays Co." },
        { to: "/lockhart", name: "Lockhart", county: "Caldwell Co." },
        { to: "/bastrop", name: "Bastrop", county: "Bastrop Co." },
      ]}
      highlights={[
        { to: "/awnings", title: "Custom Awnings", desc: "Welded aluminum frames + Sunbrella canvas spec'd for Central Texas sun and wind." },
        { to: "/pergola-covers", title: "Patio & Pergola Covers", desc: "Backyard shade structures sized for Central Texas patios and outdoor kitchens." },
        { to: "/sail-shades", title: "Sail Shades", desc: "Tensioned shade for schools, parks, pools and commercial play areas." },
        { to: "/commercial-awnings", title: "Commercial Awnings", desc: "Storefront, restaurant and brewery canopies built to city sign code." },
        { to: "/curtains", title: "Outdoor Curtains & Drop Panels", desc: "Roll-down panels that turn a covered patio into a 4-season space." },
        { to: "/custom-fabrication", title: "Welding & Fabrication", desc: "Custom aluminum frames, brackets and one-off shade builds in-house." },
      ]}
      faqs={[
        {
          q: "Do you really drive to Austin and Round Rock from Seguin?",
          a: "Yes. We install across the entire I-35 corridor every week and price the trip into the quote — no separate travel fee surprises.",
        },
        {
          q: "Will a fabric awning survive Central Texas hail and wind?",
          a: "Our welded aluminum frames are engineered for sustained 60+ mph gusts. We use Sunbrella® marine-grade canvas with reinforced corners; small hail typically sheds with no damage.",
        },
        {
          q: "How long does a Central Texas custom awning project take?",
          a: "From signed quote to install we average 3–5 weeks depending on size and finish. Patterning is done on-site, fabrication and sewing happen in Seguin.",
        },
        {
          q: "Can you match a downtown Austin storefront's brand colors?",
          a: "Yes — Sunbrella® offers 100+ solids and stripes. We provide swatch books and proof renderings before fabrication for any commercial Central Texas job.",
        },
      ]}
    />
  ),
});
